import { FormControl, Grid, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import useNurseFeatureData, { DEACTIVATE_POST_SHIFTS_CHECKBOX_ID } from "../../pages/Profile/v2/GeneralDetails/useNurseFeatureData";
import { useScheduleNurseDeactivation, useSetEmployeeStatus } from "../../pages/Profile/v2/hooks";
import { INK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { EMPLOYEE_STATUS_REASON_PRESETS, reasonToPresetCode } from "../../utils/employeeStatusReason";
import { getNurseEffectiveStatus, getNurseStatusEndDate, getNurseStatusReason, getNurseStatusStartDate } from "../../utils/formatters";
import { EffectiveEmployeeStatus, SettableEmployeeStatus, type TeamNurse } from "../../utils/models";
import { DateInput } from "../UIToolkit/Inputs/DateInput";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import type { DropdownItem } from "../UIToolkit/Inputs/SystemDropDown/types";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { DateRangeSelector } from "../UIToolkit/molecules/DateRangeSelector";
import SystemDialog, { type DialogCheckedState } from "../UIToolkit/Popups/Dialog";

const OTHER_REASON_SENTINEL = "__other__";
const REASON_UNSELECTED_SENTINEL = "__unselected__";
const MAX_REASON_CHARS = 300;

type Props = {
	open: boolean;
	nurse: TeamNurse;
	departmentId: string;
	onClose: () => void;
	onSaved?: () => void;
};

const statusToggleSx = (selected: boolean) => (theme: Theme) => ({
	textTransform: "none" as const,
	fontWeight: 600,
	fontSize: theme.typography.body2.fontSize,
	color: selected ? SURFACE : INK.SECONDARY,
	bgcolor: selected ? theme.palette.primary.main : SURFACE,
	border: `1px solid ${NEUTRAL[300]}`,
	"&:hover": { bgcolor: selected ? theme.palette.primary.main : NEUTRAL[100] },
	"&.Mui-selected": {
		bgcolor: theme.palette.primary.main,
		color: SURFACE,
		"&:hover": { bgcolor: theme.palette.primary.dark },
	},
});

const fieldLabelSx = (theme: Theme) => ({
	color: alpha(INK.SECONDARY, 0.5),
	fontSize: theme.typography.caption.fontSize,
	fontWeight: 600,
});

/**
 * Admin dialog to set a nurse's per-department status (OnLeave / Suspended / Inactive),
 * with a reason preset (persisted as a stable code) or Other free text, and a status window (start defaults to
 * today, future dates allowed; end optional). Built on SystemDialog, cloning AddDayOffDialog.
 */
export const EditEmployeeStatusDialog: FC<Props> = ({ open, nurse, departmentId, onClose, onSaved }) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const { setEmployeeStatus, isSettingEmployeeStatus } = useSetEmployeeStatus();
	const { scheduleDeactivation, isSchedulingDeactivation } = useScheduleNurseDeactivation();

	// Dropdown values are the STABLE reason codes (persisted verbatim); labels are display-only.
	// A disabled placeholder leads so a fresh dialog starts unselected — the admin must actively
	// pick a reason (enforced in `confirmDisabledReason`) rather than silently persisting a default.
	const reasonItems = useMemo<DropdownItem<string>[]>(
		() => [
			{ value: REASON_UNSELECTED_SENTINEL, label: t("employee.status.reason.select"), id: REASON_UNSELECTED_SENTINEL, disabled: true },
			...EMPLOYEE_STATUS_REASON_PRESETS.map((preset) => ({ value: preset.code, label: t(preset.labelKey), id: preset.code })),
			{ value: OTHER_REASON_SENTINEL, label: t("employee.status.reason.other"), id: OTHER_REASON_SENTINEL },
		],
		[t],
	);

	const existingReason = getNurseStatusReason(nurse, departmentId);
	const existingPresetCode = reasonToPresetCode(existingReason);

	// Seed the toggle from the nurse's current effective status so re-opening a Suspended (or
	// On Leave / Inactive) nurse shows their existing status selected; only a fresh set (currently
	// Active) defaults to On Leave.
	const initialStatus = useMemo<SettableEmployeeStatus>(() => {
		const effective = getNurseEffectiveStatus(nurse, departmentId);
		switch (effective) {
			case EffectiveEmployeeStatus.OnLeave:
				return SettableEmployeeStatus.OnLeave;
			case EffectiveEmployeeStatus.Suspended:
				return SettableEmployeeStatus.Suspended;
			case EffectiveEmployeeStatus.Inactive:
				return SettableEmployeeStatus.Inactive;
			default:
				return SettableEmployeeStatus.OnLeave;
		}
	}, [nurse, departmentId]);

	const [status, setStatus] = useState<SettableEmployeeStatus>(initialStatus);
	const [reasonChoice, setReasonChoice] = useState<string>(existingPresetCode ?? (existingReason ? OTHER_REASON_SENTINEL : REASON_UNSELECTED_SENTINEL));
	const [otherReason, setOtherReason] = useState<string>(existingPresetCode ? "" : (existingReason ?? ""));
	const [startDate, setStartDate] = useState<DateTime>(getNurseStatusStartDate(nurse, departmentId) ?? DateTime.now().startOf("day"));
	const [endDate, setEndDate] = useState<DateTime | null>(getNurseStatusEndDate(nurse, departmentId));

	const isInactive = status === SettableEmployeeStatus.Inactive;
	const isSaving = isSettingEmployeeStatus || isSchedulingDeactivation;

	// Inactive == permanent off-boarding through the existing deactivate flow. The "I understand"
	// (future shifts / recurring template) and "post removed shifts" checkboxes come from the same
	// source the Activate section uses, keyed off the chosen start date so the warnings match the date.
	// Only Inactive removes shifts, so the server is asked about them only while it's the chosen status.
	const { deactivateNursesCheckboxes, deactivationBlockedReason, setDeactivationDate } = useNurseFeatureData(isInactive ? nurse : undefined);
	const deactivationDateStr = useMemo(() => startDate.toFormat("yyyy-MM-dd"), [startDate]);
	useEffect(() => {
		setDeactivationDate(deactivationDateStr);
	}, [deactivationDateStr, setDeactivationDate]);

	// Persist the stable preset code (or free text for "Other") — never the translated label.
	const resolvedReason = useMemo<string | null>(() => {
		if (reasonChoice === REASON_UNSELECTED_SENTINEL) return null;
		if (reasonChoice === OTHER_REASON_SENTINEL) {
			return otherReason.trim() === "" ? null : otherReason.trim();
		}
		return reasonChoice;
	}, [reasonChoice, otherReason]);

	const confirmDisabledReason = useMemo<string | null>(() => {
		if (isSaving) return t("saving");
		if (isInactive) return deactivationBlockedReason;
		if (endDate && endDate.startOf("day") <= startDate.startOf("day")) return t("employee.status.invalid.range");
		if (reasonChoice === REASON_UNSELECTED_SENTINEL) return t("employee.status.reason.required");
		if (reasonChoice === OTHER_REASON_SENTINEL && otherReason.trim() === "") return t("employee.status.reason.required");
		return null;
	}, [isSaving, isInactive, deactivationBlockedReason, endDate, startDate, reasonChoice, otherReason, t]);

	const handleConfirm = useCallback(
		async (checkedState?: DialogCheckedState[]) => {
			if (status === SettableEmployeeStatus.Inactive) {
				// today => immediate deactivation, future => scheduled (matches the Activate section's distinction).
				const postShifts = checkedState?.find((checkbox) => checkbox.id === DEACTIVATE_POST_SHIFTS_CHECKBOX_ID)?.checked === true;
				await scheduleDeactivation({
					departmentId,
					nurseId: nurse.id,
					deactivationDate: deactivationDateStr,
					postShifts,
				});
			} else {
				await setEmployeeStatus({
					departmentId,
					nurseId: nurse.id,
					status,
					statusReason: resolvedReason,
					startDate: startDate.toFormat("yyyy-MM-dd"),
					endDate: endDate ? endDate.toFormat("yyyy-MM-dd") : null,
				});
			}
			onSaved?.();
			onClose();
		},
		[setEmployeeStatus, scheduleDeactivation, departmentId, nurse.id, status, resolvedReason, startDate, endDate, deactivationDateStr, onSaved, onClose],
	);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("employee.status.edit.title.named", { name: formatPersonName(nurse) })}
			dataTestId="edit-employee-status-dialog"
			cancel={t("cancel")}
			confirm={isInactive ? t("employee.status.inactive.confirm") : t("save")}
			onConfirm={handleConfirm}
			confirmTooltip={confirmDisabledReason ?? undefined}
			confirmDisabled={!!confirmDisabledReason}
			confirmLoading={isSaving}
			checkboxes={isInactive ? deactivateNursesCheckboxes : undefined}
			body={
				<Grid container spacing={1.5} sx={{ pt: 1 }}>
					<Grid size={12}>
						<FormControl fullWidth>
							<Typography sx={fieldLabelSx}>{t("employee.status.field.status")}</Typography>
							<ToggleButtonGroup
								value={status}
								exclusive
								onChange={(_, next) => next !== null && setStatus(next)}
								fullWidth
								data-testid="edit-employee-status-toggle"
							>
								{Object.values(SettableEmployeeStatus).map((value) => (
									<ToggleButton key={value} value={value} data-testid={`edit-employee-status-${value.toLowerCase()}`} sx={statusToggleSx(status === value)}>
										{t(`employee.status.chip.${value === "OnLeave" ? "on_leave" : value.toLowerCase()}`)}
									</ToggleButton>
								))}
							</ToggleButtonGroup>
						</FormControl>
					</Grid>

					{!isInactive && (
						<Grid size={12}>
							<Typography sx={fieldLabelSx}>{t("employee.status.field.reason")}</Typography>
							<SystemDropdown
								items={reasonItems}
								selectedItem={reasonChoice}
								onChange={(value) => value && setReasonChoice(value)}
								valueToString={(value) => value}
								formControlSx={{ width: "100%" }}
								data-testid="edit-employee-status-reason-select"
							/>
						</Grid>
					)}

					{!isInactive && reasonChoice === OTHER_REASON_SENTINEL && (
						<Grid size={12}>
							<SystemValidationTextField
								reserveHeightForHelperText={false}
								dataTestId="edit-employee-status-other-reason-input"
								injectedProps={{ multiline: true, rows: 3, slotProps: { htmlInput: { maxLength: MAX_REASON_CHARS } } }}
								formControlSx={{ width: "100%" }}
								value={otherReason}
								onChange={(event) => setOtherReason(event.target.value)}
								placeholder={t("employee.status.reason.other.placeholder")}
							/>
						</Grid>
					)}

					{isInactive ? (
						<Grid size={12}>
							<Typography sx={fieldLabelSx}>{t("employee.status.inactive.date.label")}</Typography>
							<DateInput
								dataTestId="edit-employee-status-inactive-date-input"
								value={startDate}
								onChange={(date) => setStartDate(date.startOf("day"))}
								minDate={DateTime.now().startOf("day")}
								formControlSx={{ width: "100%" }}
							/>
							<Typography sx={(theme) => ({ ...fieldLabelSx(theme), mt: 1, fontWeight: 500 })}>{t("employee.status.inactive.date.hint")}</Typography>
						</Grid>
					) : (
						<Grid size={12} sx={{ mt: 1 }}>
							<DateRangeSelector
								padding={0}
								startDate={startDate}
								endDate={endDate ?? undefined}
								onStartDateChange={(date) => {
									setStartDate(date);
									if (endDate && endDate.startOf("day") <= date.startOf("day")) setEndDate(null);
								}}
								onEndDateChange={(date) => setEndDate(date)}
								onClear={() => setEndDate(null)}
								removeBackground
								removeBorder
								containerSX={{ width: "100%" }}
								dateInputSX={{ width: "100%" }}
							/>
						</Grid>
					)}
				</Grid>
			}
		/>
	);
};

export default EditEmployeeStatusDialog;
